import { Fragment } from "react";
import { Link, useLoaderData } from "react-router-dom";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { FadeIn, Section } from "../components/ui";
import { NewsletterForm } from "../components/NewsletterForm";
import { Seo } from "../components/Seo";
import {
  fetchForRoute,
  getPosts,
  getPostCategory,
  getFeaturedImage,
  calculateReadTime,
  formatDate,
  type WPPost,
} from "../lib/wordpress";

export async function loader() {
  return fetchForRoute("/blog", () => getPosts(50));
}

export function BlogList() {
  const posts = useLoaderData() as WPPost[];

  return (
    <main className="relative z-10 pt-32 pb-24">
      <Seo
        title="Blog | Insights de Marketing e Branding — P2A"
        description="Artigos e insights sobre marketing estratégico, branding, lançamentos digitais e automação com IA para negócios físicos e digitais."
        path="/blog"
      />
      <Section className="py-16">
        <FadeIn>
          <Link to="/#blog" className="inline-flex items-center gap-2 text-brand-light/50 hover:text-brand-accent transition-colors font-bold uppercase tracking-widest text-sm mb-12 group">
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" /> Voltar
          </Link>
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 border-b border-brand-light/20 pb-8">
            <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tighter">
              Todos os <span className="font-highlight italic text-brand-accent lowercase font-normal">insights</span>
            </h1>
          </div>
        </FadeIn>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map((post, index) => {
            const image = getFeaturedImage(post);
            const category = getPostCategory(post);
            const readTime = calculateReadTime(post.content.rendered);
            const date = formatDate(post.date);

            return (
              <Fragment key={post.id}>
                <FadeIn delay={Math.min(index * 0.05, 0.3)}>
                  {/* No trailing slash — see the comment on the equivalent
                      Link in components/Blog.tsx for why. */}
                  <Link to={`/blog/${post.slug}`} className="block h-full">
                    <article className="group cursor-pointer flex flex-col h-full bg-brand-light/5 rounded-[2rem] overflow-hidden border border-brand-light/10 hover:border-brand-accent/30 hover:-translate-y-2 hover:shadow-[0_20px_40px_rgba(242,125,38,0.1)] transition-all duration-500">
                      <div className="relative overflow-hidden aspect-[4/3]">
                        <div className="absolute inset-0 bg-brand-dark/40 group-hover:bg-transparent transition-colors duration-500 z-10 mix-blend-multiply"></div>
                        {image ? (
                          <img
                            src={image}
                            alt={post.title.rendered}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                          />
                        ) : (
                          <div className="w-full h-full bg-brand-light/10" />
                        )}
                      </div>

                      <div className="p-8 flex flex-col flex-grow bg-gradient-to-b from-brand-light/5 to-transparent">
                        <div className="flex items-center gap-3 text-brand-light/50 text-sm font-medium mb-4">
                          <span>{date}</span>
                          <span className="w-1 h-1 rounded-full bg-brand-light/30"></span>
                          <span>{readTime}</span>
                        </div>
                        <h2
                          className="text-2xl font-bold group-hover:text-brand-accent transition-colors leading-tight font-display mb-8 flex-grow"
                          dangerouslySetInnerHTML={{ __html: post.title.rendered }}
                        />
                        <div className="flex items-center gap-2 text-brand-accent font-bold uppercase tracking-widest text-sm mt-auto group-hover:text-brand-light transition-colors">
                          Ler artigo <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                        </div>
                      </div>
                    </article>
                  </Link>
                </FadeIn>
              </Fragment>
            );
          })}
        </div>
      </Section>
      <NewsletterForm />
    </main>
  );
}
